import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonList,
  IonPage,
  IonSpinner,
  IonTitle,
  IonToolbar,
  useIonViewDidLeave,
  useIonViewWillEnter,
} from '@ionic/react'
import React, { BaseSyntheticEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { getCriticalCareSelector } from '../selectors'
import { chevronBackOutline } from 'ionicons/icons'
import { saveCriticalCare, setDateTime } from '../store/criticalCare.actions'
import { TabConstants } from '../../../../components/tabs/Tabs.constants'
import PCRHeader from '../../../../components/pcrHeader/PCRHeader.component'
import { setDeleteProcedure, setProcedureLoaderItem } from '../../../timeline/store/timeline.actions'
import ProcedureHeaderComponent from '../../../../components/procedure/ProcedureHeader.component'
import ProcedureFooterComponent from '../../../../components/procedure/ProcedureFooter.component'
import { getPatientTimelineSelector } from '../../../timeline/selectors/patientTimeline.selector'
import { compareObjectsIfEqual, filterOutObjectKeys } from '../../../../utilities/object.util'
import { initialCriticalCareState } from '../store/criticalCare.reducers'
import { showAlert } from '../../../../components/alert/store/alert.actions'
import CriticalCareContent from './pageContent/criticalCare.content'
import { goBack } from '../../../../utilities/navigation.util'
import { setIsFirstVisit } from '../../../../components/procedure/store/procedure.actions'
import { IServiceConfig } from '../../../configurations/store/configurations.types'
import { getServiceConfigSelector } from '../../../configurations/selectors/serviceConfig.selector'
import { IAlertState } from '../../../../components/alert/store/alert.types'
import { getCurrentDate, getCurrentTime } from '../../../../utilities'
import { TimelineConstants } from '../../../timeline/store/timeline.constants'

const CriticalCare: React.FC = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const history = useHistory()
  const timelineState = useSelector(getPatientTimelineSelector)
  const { procedureLoader } = timelineState
  const criticalCare = useSelector(getCriticalCareSelector)

  const {
    date,
    time,
    assessedBy,
    procedureComplications,
    response,
    responsePNNV,
    authorization,
    comments,
    rolePerforming,
    rolePerformingPNNV,
    successfulAttempt,
    pta,
    attempts,
    isEkgImport,
    procedurePerformedByAnotherUnit,
    procedurePerformedByAnotherUnitPNNV,
  } = criticalCare

  const serviceConfigData: IServiceConfig = useSelector(getServiceConfigSelector)
  const { DISPLAY_SECONDS } = serviceConfigData
  const displaySeconds = !!DISPLAY_SECONDS

  const dateTimeValidationCheck = () => {
    if (!date || !time) {
      dispatch(showAlert({ header: '', message: 'Date / Time is required' }))
      return false
    }
    return true
  }

  const initialStateCheck = () => {
    if (pta && ((date && time) || (!date && !time))) return true
    const fieldsToFilter = ['key', 'addToTimeline', 'updateInputRows', 'nemsisCode', 'snomedId', 'action']
    const currentState = filterOutObjectKeys(criticalCare, fieldsToFilter)
    const initalState = filterOutObjectKeys(initialCriticalCareState, fieldsToFilter)
    if (!compareObjectsIfEqual(currentState, initalState)) {
      return dateTimeValidationCheck()
    }
    return true
  }

  const onSaveClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if (!pta && (!date || !time)) {
      dateTimeValidationCheck()
      return
    }
    const dateTimeValidationCheckResult = initialStateCheck()
    if (dateTimeValidationCheckResult) {
      history.push(TabConstants.TIMELINE_TAB)
    }
  }

  const onBackClick = (e: BaseSyntheticEvent) => {
    e.preventDefault()
    if ((!date || !time) && !pta) {
      const alert: IAlertState = {
        message: t('common.saveTimelineEntry'),
        buttons: [
          {
            text: t('common.buttons.Yes'),
            handler: () => {
              const date = getCurrentDate()
              let time = getCurrentTime()
              if (!displaySeconds) {
                time = time.replace(/(:\d+)(:\d+)$/, '$1:00')
              }

              dispatch({ type: 'criticalCare/SET_TIME', time: time })
              dispatch({ type: 'criticalCare/SET_DATE', date: date })
              dispatch(saveCriticalCare())
              goBack()
            },
          },
          {
            text: t('common.buttons.No'),
            role: 'destructive',
            handler: () => {
              const itemToRemove = {
                key: criticalCare.key,
                rowActionType: TimelineConstants.SET_CRITICAL_CARE_PROCEDURE_ITEM,
              }
              dispatch(setDeleteProcedure(true))
              dispatch({ type: TimelineConstants.DELETE_TIMELINE_ITEM, payload: itemToRemove })
              goBack()
            },
          },
        ],
      }
      dispatch(showAlert(alert))
      dispatch(setProcedureLoaderItem(false))
    } else {
      const dateTimeValidationCheck = initialStateCheck()
      if (dateTimeValidationCheck) {
        goBack()
      }
      dispatch(setProcedureLoaderItem(false))
    }
  }

  useIonViewWillEnter(() => {
    if (date || time) dispatch(setDateTime(time, date))

    return
  })

  useIonViewDidLeave(() => {
    dispatch(setIsFirstVisit(false))
  })

  return (
    <IonPage id='CriticalCarePage'>
      <PCRHeader />
      <IonHeader>
        <IonToolbar color='light'>
          <IonButtons slot='start'>
            <IonButton data-testid='Back' fill='clear' onClick={onBackClick} color='primary-alt'>
              <IonIcon icon={chevronBackOutline} />
              {t('common.buttons.Back')}
            </IonButton>
          </IonButtons>
          <IonButtons slot='end'>
            <IonButton data-testid='Save' fill='clear' onClick={onSaveClick} color='primary-alt'>
              {t('common.buttons.Save')}
            </IonButton>
          </IonButtons>
          <IonTitle>{t('criticalCare.criticalCare')}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent color='light'>
        {procedureLoader ? (
          <IonSpinner className='procedure-loader' data-testid='Spinner' />
        ) : (
          <IonList id='CriticalCareProcedure'>
            <ProcedureHeaderComponent
              isPHI
              procedureType={'criticalCare'}
              procedureDate={date}
              procedureTime={time}
              assessedBy={assessedBy}
              rolePerforming={rolePerforming}
              rolePerformingPNNV={rolePerformingPNNV}
              successfulAttempt={successfulAttempt}
              pta={pta}
              attempts={attempts}
              hideAttemptField={true}
              hideSuccessfulAttempt={true}
              isEkgImport={isEkgImport}
              procedurePerformedByAnotherUnit={procedurePerformedByAnotherUnit}
              procedurePerformedByAnotherUnitPNNV={procedurePerformedByAnotherUnitPNNV}
            />
            <CriticalCareContent />
            <ProcedureFooterComponent
              isPHI
              procedureType={'criticalCare'}
              procedureComplications={procedureComplications}
              response={response}
              responsePNNV={responsePNNV}
              authorization={authorization}
              comments={comments}
            />
          </IonList>
        )}
      </IonContent>
    </IonPage>
  )
}

export default CriticalCare
